import React, { useState } from "react";
import { ColorV2 } from "config/colors";
import { CustomIcon } from "components/icon/customIcon.component";
import SimflexityLogo from "images/icons/svg/v2/simflexity-logo.svg";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Badge } from "components/buttons/buttons.styled";
import { useAuthStore } from "stores/authStore/authStore";
import { CompanyRole } from "model/User";
import {
  Container,
  IconWrap,
  ItemLink,
  Items,
  Label,
  Logo,
} from "./navbarMenu.styles";

export interface MenuItem {
  value: string;
  label: string;
  iconName: string;
  href: string;
  badgeCount?: number;
}

interface MenuProps {
  items: MenuItem[];
  activeValue: string;
  logoSrc?: string;
  onItemClick?: (
    item: MenuItem,
    event: React.MouseEvent<HTMLAnchorElement>,
  ) => void;
}

interface NavbarMenuItemProps {
  item: MenuItem;
  isActive: boolean;
  onClick: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

const NavbarMenuItem = ({
  item,
  isActive,
  onClick,
}: NavbarMenuItemProps): React.ReactElement => {
  const [isHovered, setIsHovered] = useState(false);
  const iconColor = isActive
    ? ColorV2.Blue1
    : isHovered
      ? ColorV2.Blue3
      : ColorV2.Blue5;

  return (
    <ItemLink
      to={item.href}
      aria-current={isActive ? "page" : undefined}
      $isActive={isActive}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <IconWrap>
        <CustomIcon name={item.iconName} size="24px" color={iconColor} />
        {typeof item.badgeCount === "number" && item.badgeCount > 0 && (
          <Badge>{item.badgeCount}</Badge>
        )}
      </IconWrap>
      <Label $isActive={isActive}>{item.label}</Label>
    </ItemLink>
  );
};

export const NavbarMenu = ({
  items,
  activeValue,
  logoSrc = SimflexityLogo,
  onItemClick,
}: MenuProps): React.ReactElement => {
  const { t } = useTranslation();
  const [authState] = useAuthStore();

  return (
    <Container role="navigation" aria-label={t("DesignComponents.MainMenu")}>
      {authState.user?.role !== CompanyRole.COMPANY_TIMESTAMP && (
        <Link to="/company/schedule">
          <Logo src={logoSrc} alt="Simflexity logo" />
        </Link>
      )}
      {authState.user?.role == CompanyRole.COMPANY_TIMESTAMP && (
        <Link to="/company/stampclock">
          <Logo src={logoSrc} alt="Simflexity logo" />
        </Link>
      )}
      <Items>
        {items.map((item) => (
          <NavbarMenuItem
            key={item.value}
            item={item}
            isActive={item.value === activeValue}
            onClick={(event) => onItemClick?.(item, event)}
          />
        ))}
      </Items>
    </Container>
  );
};
